import { useCallback, useState } from 'react';
import { MutationFn, useClient, useLoaderToast, useLocale } from 'shared-ui';

import { AppMessages } from 'i18n/messages';

/**
 * Hook for downloading report files using native fetch API.
 * Bypasses axios to avoid response interceptor issues with binary data.
 * Works reliably in both HTTP (webkd.local) and HTTPS environments.
 */
export const useReportDownload = <TRequest>({
  mutationFn,
  buildRequestBody,
  onEmailModalClose,
  reportNameId,
}: {
  mutationFn: MutationFn<TRequest, Blob>;
  buildRequestBody: (emails?: string[]) => TRequest;
  onEmailModalClose: () => void;
  reportNameId: keyof typeof AppMessages;
}) => {
  const [hasNoDataError, setHasNoDataError] = useState(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useLocale();
  const { authStorage } = useClient();
  const { load, update, close } = useLoaderToast('generateReport');

  const getFormattedFilename = useCallback(() => {
    // Format timestamp in Europe/Warsaw timezone (same as backend)
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Warsaw',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const timestamp = formatter.format(now).replace(' ', 'T').replace(/:/g, '_');
    return `${formatMessage({ id: reportNameId })} - ${timestamp}.xlsx`;
  }, [formatMessage, reportNameId]);

  /**
   * Fallback download using anchor element - works in all environments including HTTP
   */
  const fallbackDownload = useCallback(
    (blob: Blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.download = getFormattedFilename();
      document.body.appendChild(link);
      link.click();

      // Cleanup after download starts
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(blobUrl);
      }, 200);

      update(formatMessage({ id: AppMessages['reports.download.success'] }));
    },
    [getFormattedFilename, update, formatMessage],
  );

  /**
   * Trigger file download - uses File System Access API if available, falls back to anchor element
   */
  const triggerDownload = useCallback(
    (blob: Blob) => {
      // Check if File System Access API is available (HTTPS or localhost only)
      const isFileSystemAccessAvailable = typeof window.showSaveFilePicker === 'function' && window.isSecureContext;

      if (isFileSystemAccessAvailable) {
        // Use File System Access API for better UX
        window
          .showSaveFilePicker({
            suggestedName: getFormattedFilename(),
            startIn: 'downloads',
            types: [
              {
                description: 'Excel Spreadsheet',
                accept: {
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                },
              },
            ],
          })
          .then(async (handle) => {
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            update(formatMessage({ id: AppMessages['reports.download.success'] }));
          })
          .catch((err) => {
            if (err instanceof Error && err.name === 'AbortError') {
              // User cancelled - just close the loader
              close();
              return;
            }
            // Fall through to traditional download on error
            fallbackDownload(blob);
          });
        return;
      }

      // Fallback for non-secure contexts (HTTP)
      fallbackDownload(blob);
    },
    [getFormattedFilename, formatMessage, update, close, fallbackDownload],
  );

  const handleDownload = useCallback(
    async (emails?: string[]) => {
      setIsLoading(true);
      setHasNoDataError(false);

      const isEmailMode = emails && emails.length > 0;

      load(
        formatMessage({
          id: isEmailMode ? AppMessages['reports.email.pending'] : AppMessages['reports.download.pending'],
        }),
      );

      try {
        // Get endpoint and body from mutation function
        const requestBody = buildRequestBody(emails);
        const { endpoint, body } = mutationFn(requestBody);

        // Use native fetch API to bypass axios interceptors
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const url = `${baseUrl}${endpoint}`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authStorage.accessToken ? { Authorization: `Bearer ${authStorage.accessToken}` } : {}),
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          // Try to parse error response
          let errorCode: string | undefined;
          try {
            const text = await response.text();
            const parsed = JSON.parse(text);
            errorCode = parsed?.error;
          } catch {
            // Ignore parse errors
          }
          if (errorCode === 'NO_DATA_FOR_CRITERIA') {
            setHasNoDataError(true);
          } else {
            setHasNetworkError(true);
          }

          close();
          setIsLoading(false);
          return;
        }

        // Handle email mode
        if (isEmailMode) {
          update(formatMessage({ id: AppMessages['reports.email.success'] }));
          onEmailModalClose();
          setIsLoading(false);
          return;
        }

        // Get blob from response and trigger download
        const blob = await response.blob();
        triggerDownload(blob);
      } catch {
        close();
      } finally {
        setIsLoading(false);
      }
    },
    [
      authStorage.accessToken,
      buildRequestBody,
      close,
      formatMessage,
      load,
      mutationFn,
      onEmailModalClose,
      triggerDownload,
      update,
    ],
  );

  const resetError = useCallback(() => {
    setHasNoDataError(false);
    setHasNetworkError(false);
  }, []);

  return {
    onDownload: handleDownload,
    isLoading,
    hasNoDataError,
    hasNetworkError,
    resetError,
  };
};
